import React, { useEffect, useMemo, useState } from "react";
import '../../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
  FormGroup,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link, useLocation } from "react-router-dom";

// Import services
import {
  fetchProdukKontens,
  createProdukKonten,
  updateProdukKonten,
  deleteProdukKonten,
} from '../../../services/produkKontenService';
import { fetchProduks } from '../../../services/produkService';
import { fetchTipeKonten } from '../../../services/Master/MasterTipeKonten';
import { fetchJenisSeleksi } from '../../../services/Master/MasterJenisSeleksi';

// Import components
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import DeleteModal from '../../../components/Common/DeleteModal';

const ProdukKonten = () => {
  // Meta title
  document.title = "Produk Konten";

  // Get query params
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const idProduk = queryParams.get('id_produk');

  // State management
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [produkKonten, setProdukKonten] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [tipeKontenOptions, setTipeKontenOptions] = useState([]);
  const [jenisSeleksiOptions, setJenisSeleksiOptions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    id_produk: idProduk || "",
    id_tipe_konten: "",
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [produkOptions, setProdukOptions] = useState([]);
  const [canAdd, setCanAdd] = useState(true);

  // Handle delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setProdukKonten(null);
  };

const handleConfirmDelete = async () => {
    try {
        if (produkKonten && produkKonten.id_produk_konten) {
            const response = await deleteProdukKonten(produkKonten.id_produk_konten);
            if (response.success) {
                toast.success("Produk konten berhasil dihapus");
                // Perbaikan: Buat salinan filters dan pastikan id_produk disertakan
                const updatedFilters = { ...filters, id_produk: idProduk };
                fetchData(pagination.page, pagination.per_page, updatedFilters);
            } else {
                toast.error(response.message || "Gagal menghapus produk konten");
            }
        }
    } catch (error) {
        console.error("Error deleting produk konten:", error);
        toast.error(error.message || "Gagal menghapus produk konten");
    } finally {
        handleCloseDeleteModal();
    }
};

  // Fetch data
  const fetchData = async (page = 1, per_page = 10, filterParams = {}) => {
    try {
      setLoading(true);
      const response = await fetchProdukKontens({
        page,
        per_page,
        filters: filterParams
      });

      const total = response.meta?.total || 0;
      const calculatedPerPage = response.meta?.per_page || per_page;
      const last_page = Math.ceil(total / calculatedPerPage);

      setData(response.data || []);
      setPagination({
        page: response.meta?.page || 1,
        per_page: calculatedPerPage,
        total: total,
        last_page: last_page
      });
    } catch (error) {
      console.error("Error in fetchData:", error);
      toast.error("Gagal memuat data produk konten");
    } finally {
      setLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      setLoadingDependencies(true);
      const [produkRes, tipeKontenRes] = await Promise.all([
        fetchProduks({ disable_pagination: true }),
        fetchTipeKonten({ disable_pagination: true }),
      ]);
  
      if (produkRes && produkRes.data) {
        setProdukOptions(produkRes.data);
      } else {
        toast.error("Gagal memuat opsi produk.");
      }
  
      if (tipeKontenRes && tipeKontenRes.data) {
        setTipeKontenOptions(tipeKontenRes.data);
      } else {
        toast.error("Gagal memuat opsi tipe konten.");
      }
    } catch (error) {
      console.error("Error fetching dependencies:", error);
      toast.error("Terjadi kesalahan saat memuat opsi.");
    } finally {
      setLoadingDependencies(false);
    }
  };

  const fetchJenisSeleksiByTipeKonten = async (id_tipe_konten) => {
    try {
        if (!id_tipe_konten) {
            setJenisSeleksiOptions([]);
            return;
        }
        setLoadingDependencies(true);
        // Mengirimkan id_tipe_konten sebagai parameter filter
          const response = await fetchJenisSeleksi({
                id_tipe_konten: id_tipe_konten,
                disable_pagination: true
              });
        if (response && response.data) {
            setJenisSeleksiOptions(response.data);
        } else {
            setJenisSeleksiOptions([]);
            toast.error("Gagal memuat opsi jenis seleksi.");
        }
    } catch (error) {
        console.error("Error fetching jenis seleksi:", error);
        toast.error("Terjadi kesalahan saat memuat jenis seleksi.");
    } finally {
        setLoadingDependencies(false);
    }
};
  
  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id_produk: produkKonten?.id_produk || idProduk || "",
      id_tipe_konten: produkKonten?.id_tipe_konten || "",
      id_jenis_seleksi: produkKonten?.id_jenis_seleksi || "",
    },

    validationSchema: Yup.object({
      id_produk: Yup.string().required("Produk wajib dipilih"),
      id_tipe_konten: Yup.string().required("Tipe konten wajib dipilih"),
      id_jenis_seleksi: Yup.string().nullable(),
    }),

    onSubmit: async (values) => {
      try {
        let response;
        if (isEdit) {
          response = await updateProdukKonten(produkKonten.id_produk_konten, values);
        } else {
          response = await createProdukKonten(values);
        }

        if (response.success) {
          toast.success(response.message || (isEdit ? "Produk konten berhasil diperbarui" : "Produk konten berhasil ditambahkan"));
          setModal(false);
          fetchData(pagination.page, pagination.per_page, filters);
        } else {
          if (response.errors) {
            Object.keys(response.errors).forEach(key => {
              response.errors[key].forEach(msg => {
                toast.error(`${key}: ${msg}`);
              });
            });
          } else {
            toast.error(response.message || "Gagal menyimpan produk konten");
          }
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        toast.error("Terjadi kesalahan saat menyimpan data.");
      }
    },
  });

  // Effects
  useEffect(() => {
    if (idProduk) {
      setCanAdd(true);
      setFilters(prev => ({ ...prev, id_produk: idProduk }));
    } else {
      setCanAdd(false);
      setFilters(prev => ({ ...prev, id_produk: "" }));
      toast.error("ID Produk tidak ditemukan di URL. Silakan kembali ke halaman produk.");
    }
  }, [idProduk]);

  useEffect(() => {
    fetchData(1, pagination.per_page, filters);
  }, [filters]);

  useEffect(() => {
    fetchDependencies();
  }, []);

  useEffect(() => {
    if (modal) {
        const selectedTipeKontenId = validation.values.id_tipe_konten;
        fetchJenisSeleksiByTipeKonten(selectedTipeKontenId);
    }
  }, [validation.values.id_tipe_konten, modal]);

  // Handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    const defaultFilters = {
      id_produk: idProduk || "",
      id_tipe_konten: "",
    };
    setFilters(defaultFilters);
  };

  const openAddModal = () => {
    if (!canAdd) {
      toast.error("Penambahan produk konten hanya diizinkan melalui halaman produk.");
      return;
    }
    setProdukKonten(null);
    setIsEdit(false);
    validation.resetForm();
    validation.setFieldValue("id_produk", idProduk);
    validation.setFieldValue("id_jenis_seleksi", "");
    setModal(true);
  };

  const openEditModal = (data) => {
    setProdukKonten(data);
    setIsEdit(true);
    validation.setValues({
      id_produk: data.id_produk || idProduk || "",
      id_tipe_konten: data.id_tipe_konten || "",
      id_jenis_seleksi: data.id_jenis_seleksi || "",
    });
    setModal(true);
  };

  const onClickDelete = (data) => {
    setProdukKonten(data);
    setDeleteModal(true);
  };

  // Columns
const columns = useMemo(
  () => [
    {
      header: "Nama Produk",
      accessorKey: "nama_produk",
      enableColumnFilter: false,
      cell: (cellProps) => {
        return cellProps.row.original.nama_produk || "-";
      },
    },
    {
      header: "Tipe Konten",
      accessorKey: "tipe_konten", // Mengubah dari "tipe_konten.tipe_konten"
      enableColumnFilter: false,
      cell: (cellProps) => {
        return cellProps.row.original.tipe_konten || "-";
      },
    },
    {
      header: "Jenis Seleksi",
      accessorKey: "nama_seleksi", // Mengubah dari "jenis_seleksi.jenis_seleksi"
      enableColumnFilter: false,
      cell: (cellProps) => {
        return cellProps.row.original.nama_seleksi || "-";
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      enableColumnFilter: false,
      cell: (cellProps) => {
        const rawDate = cellProps.getValue();
        if (rawDate) {
          const date = new Date(rawDate);
          return date.toISOString().split("T")[0];
        }
        return "-";
      },
    },
    {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
            <Link
              to="#"
              className="btn btn-sm btn-soft-info"
              onClick={(e) => {
                e.preventDefault();
                openEditModal(cellProps.row.original);
              }}
              id={`edittooltip-${cellProps.row.original.id_produk_konten}`}
            >
              <i className="mdi mdi-pencil-outline" />
              <UncontrolledTooltip
                placement="top"
                target={`edittooltip-${cellProps.row.original.id_produk_konten}`}
              >
                Edit
              </UncontrolledTooltip>
            </Link>
            {/* PERBAIKAN: Mengganti <Link> dengan <button> */}
            <button
              className="btn btn-sm btn-soft-danger"
              onClick={() => onClickDelete(cellProps.row.original)}
              id={`deletetooltip-${cellProps.row.original.id_produk_konten}`}
            >
              <i className="mdi mdi-delete-outline" />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_produk_konten}`}>
                Delete
              </UncontrolledTooltip>
            </button>
          </div>
        )
      }
  ],
  []
);

  // Custom Pagination
  const CustomPagination = () => {
    const { page, last_page, total, per_page } = pagination;
    const handlePageChange = (newPage) => {
      if (newPage >= 1 && newPage <= last_page && newPage !== page) {
        fetchData(newPage, per_page, filters);
      }
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari total{' '}
          <span className="fw-semibold">{total}</span> entri
        </div>
        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink previous onClick={() => handlePageChange(page - 1)} />
            </PaginationItem>
            {Array.from({ length: last_page }, (_, i) => i + 1).map(pageNum => (
              <PaginationItem key={pageNum} active={pageNum === page}>
                <PaginationLink onClick={() => handlePageChange(pageNum)}>
                  {pageNum}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem disabled={page === last_page}>
              <PaginationLink next onClick={() => handlePageChange(page + 1)} />
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Master Data"
            breadcrumbItem={idProduk ? "Detail Produk Konten" : "Master Produk Konten"}
          />
          {
            loading ? <Spinners setLoading={setLoading} />
              :
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody className="border-bottom">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0 card-title flex-grow-1">
                          {idProduk ? `List Produk Konten untuk Produk ID: ${idProduk}` : "List Semua Produk Konten"}
                        </h5>
                        <div className="d-flex align-items-center">
                          <Button color="primary" onClick={openAddModal} disabled={!canAdd}>
                            <i className="mdi mdi-plus me-1"></i>
                            Tambah
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                    <CardBody>
                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false}
                        isPagination={false}
                        customPageSize={pagination.per_page}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      />
                      <CustomPagination />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
          }

          {/* Modal for Add/Edit Produk Konten */}
          <Modal isOpen={modal} toggle={() => setModal(false)}>
            <ModalHeader toggle={() => setModal(false)} tag="h4">
              {isEdit ? "Edit Produk Konten" : "Tambah Produk Konten"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="id_tipe_konten">Tipe Konten</Label>
                      <Input
                        name="id_tipe_konten"
                        type="select"
                        className="form-control"
                        onChange={(e) => {
                            validation.handleChange(e);
                            validation.setFieldValue("id_jenis_seleksi", "");
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.id_tipe_konten || ""}
                        invalid={!!(validation.touched.id_tipe_konten && validation.errors.id_tipe_konten)}
                      >
                        <option value="">Pilih Tipe Konten</option>
                        {tipeKontenOptions.map(option => (
                          <option key={option.id_tipe_konten} value={option.id_tipe_konten}>
                            {option.tipe_konten}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>{validation.errors.id_tipe_konten}</FormFeedback>
                    </div>
                  </Col>
                </Row>
                {/* Tampilan kondisional untuk Jenis Seleksi */}
                {
                  (!!validation.values.id_tipe_konten) && (
                    <Row>
                      <Col md="12">
                        <div className="mb-3">
                          <Label htmlFor="id_jenis_seleksi">Jenis Seleksi</Label>
                          <Input
                            name="id_jenis_seleksi"
                            type="select"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.id_jenis_seleksi || ""}
                            invalid={!!(validation.touched.id_jenis_seleksi && validation.errors.id_jenis_seleksi)}
                          >
                            <option value="">Pilih Jenis Seleksi</option>
                            {jenisSeleksiOptions.map(option => (
                              <option key={option.id_jenis_seleksi} value={option.id_jenis_seleksi}>
                                {option.nama_seleksi}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{validation.errors.id_jenis_seleksi}</FormFeedback>
                        </div>
                      </Col>
                    </Row>
                  )
                }
                <Row>
                  <Col>
                    <div className="text-end">
                      <Button type="submit" color="primary">
                        {isEdit ? "Update" : "Tambah"}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default ProdukKonten;